import type { ApolloCache, ApolloClient } from '@apollo/client';

import { getDataFromCache } from 'src/modules/apollo/apolloCache';
import { mergeItemIntoListById } from 'src/modules/utils';
import * as Schema from 'src/types/graphql';

export const getConfirmedRoleDataFromCache = <T extends Record<string, any>>({
  cache,
}: {
  cache: ApolloCache<T>;
}) =>
  getDataFromCache({
    cache,
    query: Schema.GetConfirmedRolesDocument,
  })?.viewer?.confirmedRoles || [];

const getUnconfirmedRoleDataFromCache = <T extends Record<string, any>>({
  cache,
}: {
  cache: ApolloCache<T>;
}) =>
  getDataFromCache({
    cache,
    query: Schema.GetUnconfirmedRolesDocument,
  })?.viewer?.unconfirmedRoles || [];

const buildConfirmedRolesOptimisticResponse = ({
  roleUpdate: { id, title, company, from, to, achievements },
  apolloClient,
}: {
  // function, industry, seniority, does not need for optimistic
  // hence will omit it from here
  roleUpdate: Omit<
    Schema.UpdateRoleInput,
    'function' | 'industry' | 'seniority'
  >;
  apolloClient: ApolloClient<any>;
}) => [
  ...mergeItemIntoListById({
    list: getConfirmedRoleDataFromCache({ ...apolloClient }),
    item: {
      __typename: 'Role',
      id,
      title: {
        __typename: 'OntologyStructuredData',
        ...title,
        ontologyId: null,
      },
      company: {
        __typename: 'OntologyStructuredData',
        ...company,
        ontologyId: null,
      },
      from: {
        __typename: 'MonthYear',
        ...from,
      },
      to: to ? { __typename: 'MonthYear', ...to } : null,
      achievements: achievements || null,
      // Function and industry and not show visually in the optimistic response but are
      // needed to ensure all fields are provided. Typing this is tricky and provides
      // little value to the codebase so the error has been ignored
      // @ts-ignore
      seniority: null,
      function: null,
      industry: null,
      tracking: null,
    },
    addToFrontOfList: true,
  }),
];

export const generateUpdateRoleOptimisticResponse =
  ({ _id, apolloClient }: { _id: string; apolloClient: ApolloClient<any> }) =>
  ({
    role: { id, title, company, from, to, achievements },
  }: Schema.UpdateRoleMutationVariables) => ({
    __typename: 'Mutation',
    updateRole: {
      __typename: 'UpdateRolePayload',
      role: {
        __typename: 'Role',
        id,
      },
      viewer: {
        __typename: 'Candidate',
        _id,
        confirmedRoles: buildConfirmedRolesOptimisticResponse({
          roleUpdate: {
            id,
            title,
            company,
            from,
            to,
            achievements,
          },
          apolloClient,
        }),
      },
    },
  });

export const generateDeleteRoleOptimisticResponse =
  ({ _id, apolloClient }: { _id: string; apolloClient: ApolloClient<any> }) =>
  ({ input: { id } }: Schema.DeleteRoleMutationVariables) => {
    const confirmedRoles = getConfirmedRoleDataFromCache({ ...apolloClient });

    return {
      __typename: 'Mutation',
      deleteRole: {
        __typename: 'DeleteRolePayload',
        viewer: {
          __typename: 'Candidate',
          _id,
          confirmedRoles: confirmedRoles.filter((role) => role.id !== id),
        },
      },
    };
  };

export const generateConfirmRoleOptimisticResponse =
  ({ _id, apolloClient }: { _id: string; apolloClient: ApolloClient<any> }) =>
  ({ role: confirmedRole }: Schema.ConfirmRoleMutationVariables) => {
    const unconfirmedRoles = getUnconfirmedRoleDataFromCache({
      ...apolloClient,
    });

    return {
      __typename: 'Mutation',
      confirmRole: {
        __typename: 'ConfirmRolePayload',
        viewer: {
          __typename: 'Candidate',
          _id,
          unconfirmedRoles: unconfirmedRoles.filter(
            (role) => role.id !== confirmedRole.id,
          ),
          confirmedRoles: buildConfirmedRolesOptimisticResponse({
            roleUpdate: confirmedRole,
            apolloClient,
          }),
        },
      },
    };
  };

export const generateRejectRoleOptimisticResponse =
  ({ _id, apolloClient }: { _id: string; apolloClient: ApolloClient<any> }) =>
  ({ role: rejectedRole }: Schema.RejectRoleMutationVariables) => {
    const unconfirmedRoles = getUnconfirmedRoleDataFromCache({
      ...apolloClient,
    });

    return {
      __typename: 'Mutation',
      rejectUnconfirmedData: {
        __typename: 'RejectUnconfirmedDataPayload',
        viewer: {
          __typename: 'Candidate',
          _id,
          unconfirmedRoles: unconfirmedRoles.filter(
            (role) => role.id !== rejectedRole.id,
          ),
        },
      },
    };
  };
